/**
 * MentionInput Component
 *
 * A reusable text input with @mention autocomplete functionality.
 * Shows clean @Name format to users, stores @[Name](type:id) for backend.
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    TextField,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Chip,
    Popper,
    ClickAwayListener,
} from '@mui/material';
import { Person, Groups } from '@mui/icons-material';

export interface MentionData {
    type: 'user' | 'team';
    id: string;
    name: string;
}

export interface MentionSuggestion {
    type: 'user' | 'team';
    id: string;
    name: string;
}

interface MentionInputProps {
    value: string; // Storage format: @[Name](type:id)
    onChange: (storageValue: string, mentions: MentionData[]) => void;
    employees: Array<{ id: string; name: string }>;
    departments: Array<{ id: number; dept_name?: string; name?: string }>;
    placeholder?: string;
    rows?: number;
    isDarkMode?: boolean;
    label?: string;
    autoFocus?: boolean;
}

// Regex to find @mentions in storage format @[Name](type:id)
const STORAGE_MENTION_REGEX = /@\[([^\]]+)\]\((user|team):([^)]+)\)/g;

// Regex to find @mentions in display format @Name (followed by space or end)
const DISPLAY_MENTION_REGEX = /@([A-Za-z][A-Za-z0-9 ]*?)(?=\s|$)/g;

// Extract mentions from storage format
export const extractMentions = (content: string): MentionData[] => {
    const mentions: MentionData[] = [];
    const regex = new RegExp(STORAGE_MENTION_REGEX.source, 'g');
    let match;
    while ((match = regex.exec(content)) !== null) {
        mentions.push({
            name: match[1],
            type: match[2] as 'user' | 'team',
            id: match[3],
        });
    }
    return mentions;
};

// Convert storage format to display format
export const storageToDisplay = (storageValue: string): string => {
    return storageValue.replace(
        new RegExp(STORAGE_MENTION_REGEX.source, 'g'),
        '@$1'
    );
};

// Convert display format back to storage format using mention data
export const displayToStorage = (displayValue: string, mentions: MentionData[]): string => {
    let result = displayValue;

    // Sort mentions by name length (longest first) to avoid partial replacements
    const sortedMentions = [...mentions].sort((a, b) => b.name.length - a.name.length);

    for (const mention of sortedMentions) {
        // Replace @Name with @[Name](type:id)
        const displayPattern = new RegExp(`@${escapeRegex(mention.name)}(?=\\s|$)`, 'g');
        const storageFormat = `@[${mention.name}](${mention.type}:${mention.id})`;
        result = result.replace(displayPattern, storageFormat);
    }

    return result;
};

// Helper to escape special regex characters
const escapeRegex = (str: string): string => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const MentionInput: React.FC<MentionInputProps> = ({
    value, // Storage format
    onChange,
    employees,
    departments,
    placeholder = 'Type @ to mention someone...',
    rows = 4,
    isDarkMode = false,
    label = 'Note',
    autoFocus = false,
}) => {
    // Track mentions separately
    const [activeMentions, setActiveMentions] = useState<MentionData[]>(() => extractMentions(value));

    // Convert storage value to display value
    const displayValue = useMemo(() => storageToDisplay(value), [value]);

    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState<MentionSuggestion[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [cursorPosition, setCursorPosition] = useState(0);
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);

    // Generate suggestions based on search query
    const generateSuggestions = useCallback((query: string) => {
        const lowerQuery = query.toLowerCase();
        const results: MentionSuggestion[] = [];

        // Search employees
        employees.forEach((emp) => {
            if (emp.name && emp.name.toLowerCase().includes(lowerQuery)) {
                results.push({
                    type: 'user',
                    id: String(emp.id),
                    name: emp.name,
                });
            }
        });

        // Search departments
        departments.forEach((dept) => {
            const deptName = dept.dept_name || dept.name || '';
            if (deptName && deptName.toLowerCase().includes(lowerQuery)) {
                results.push({
                    type: 'team',
                    id: String(dept.id),
                    name: deptName,
                });
            }
        });

        return results.slice(0, 10);
    }, [employees, departments]);

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDisplayValue = e.target.value;
        const position = e.target.selectionStart || 0;
        setCursorPosition(position);

        // Check if we're in a mention context (after @)
        const textBeforeCursor = newDisplayValue.substring(0, position);
        const atIndex = textBeforeCursor.lastIndexOf('@');

        if (atIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(atIndex + 1);
            // Only show suggestions if there's no space after @ yet
            if (!/\s/.test(textAfterAt) || textAfterAt.length === 0) {
                const query = textAfterAt;
                setSearchQuery(query);
                setMentionStartIndex(atIndex);
                const newSuggestions = generateSuggestions(query);
                setSuggestions(newSuggestions);
                setShowSuggestions(newSuggestions.length > 0 || query.length === 0);
                setSelectedIndex(0);
            } else {
                setShowSuggestions(false);
            }
        } else {
            setShowSuggestions(false);
        }

        // Check if any mentions were removed
        const updatedMentions = activeMentions.filter(mention =>
            newDisplayValue.includes(`@${mention.name}`)
        );
        setActiveMentions(updatedMentions);

        // Convert to storage format and notify parent
        const storageValue = displayToStorage(newDisplayValue, updatedMentions);
        onChange(storageValue, updatedMentions);
    };

    // Handle suggestion selection
    const handleSelectSuggestion = (suggestion: MentionSuggestion) => {
        if (mentionStartIndex === -1) return;

        // Insert @Name (clean display format)
        const beforeMention = displayValue.substring(0, mentionStartIndex);
        const afterMention = displayValue.substring(cursorPosition);
        const mentionDisplay = `@${suggestion.name} `;
        const newDisplayValue = beforeMention + mentionDisplay + afterMention;

        // Add to active mentions
        const newMention: MentionData = {
            type: suggestion.type,
            id: suggestion.id,
            name: suggestion.name,
        };
        const updatedMentions = [...activeMentions, newMention];
        setActiveMentions(updatedMentions);

        // Convert to storage format and notify parent
        const storageValue = displayToStorage(newDisplayValue, updatedMentions);
        onChange(storageValue, updatedMentions);

        // Reset state
        setShowSuggestions(false);
        setMentionStartIndex(-1);
        setSearchQuery('');

        // Focus back on input
        setTimeout(() => {
            if (inputRef.current) {
                const newPosition = mentionStartIndex + mentionDisplay.length;
                inputRef.current.focus();
                inputRef.current.setSelectionRange(newPosition, newPosition);
            }
        }, 0);
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showSuggestions) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) => Math.max(prev - 1, 0));
                break;
            case 'Enter':
                if (suggestions[selectedIndex]) {
                    e.preventDefault();
                    handleSelectSuggestion(suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                break;
        }
    };

    // Handle click away
    const handleClickAway = () => {
        setShowSuggestions(false);
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box ref={anchorRef} sx={{ position: 'relative' }}>
                <TextField
                    inputRef={inputRef}
                    autoFocus={autoFocus}
                    label={label}
                    fullWidth
                    multiline
                    rows={rows}
                    value={displayValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                        },
                    }}
                />
                <Popper
                    open={showSuggestions}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    style={{ zIndex: 1300 }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            mt: 1,
                            maxHeight: 300,
                            overflow: 'auto',
                            minWidth: 250,
                            bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                        }}
                    >
                        {suggestions.length === 0 ? (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Type to search for people or teams
                                </Typography>
                            </Box>
                        ) : (
                            <List dense>
                                {suggestions.map((suggestion, index) => (
                                    <ListItem key={`${suggestion.type}-${suggestion.id}`} disablePadding>
                                        <ListItemButton
                                            selected={index === selectedIndex}
                                            onClick={() => handleSelectSuggestion(suggestion)}
                                            sx={{
                                                '&.Mui-selected': {
                                                    bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                {suggestion.type === 'user' ? (
                                                    <Person color="primary" />
                                                ) : (
                                                    <Groups color="secondary" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={suggestion.name}
                                                secondary={suggestion.type === 'user' ? 'Person' : 'Team'}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

export default MentionInput;
